// src/pages/admin/Categories.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiGrid,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiX,
  FiSave,
  FiPackage
} from 'react-icons/fi';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/products/categories/list');
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
      toast.error('Ангилал татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      
      if (editingCategory) {
        // Update
        response = await axios.put(`/api/products/categories/${editingCategory.id}`, formData);
      } else {
        // Create
        response = await axios.post('/api/products/categories', formData);
      }

      if (response.data.success) {
        toast.success(editingCategory ? 'Ангилал амжилттай шинэчлэгдлээ' : 'Ангилал амжилттай нэмэгдлээ');
        setShowModal(false);
        resetForm();
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`"${category.name}" ангилалыг устгах уу?`)) return;

    try {
      const response = await axios.delete(`/api/products/categories/${category.id}`);

      if (response.data.success) {
        toast.success('Ангилал устгагдлаа');
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setEditingCategory(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ангилал</h1>
          <p className="text-gray-600 mt-1">Барааны ангилал удирдах</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn btn-primary"
        >
          <FiPlus className="mr-2" />
          Ангилал нэмэх
        </button>
      </div>

      {/* Categories Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="spinner-lg"></div>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12">
          <FiGrid size={48} className="mx-auto mb-3 text-gray-400 opacity-50" />
          <p className="text-gray-500">Ангилал олдсонгүй</p>
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary mt-4"
          >
            <FiPlus className="mr-2" />
            Анхны ангилал нэмэх
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map(category => (
            <div
              key={category.id}
              className="card hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center flex-1">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <FiGrid className="text-blue-600" size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{category.name}</h3>
                    <p className="text-xs text-gray-500">ID: {category.id}</p>
                  </div>
                </div>
              </div>

              {category.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {category.description}
                </p>
              )}

              {/* Product Count */}
              <div className="flex items-center mb-3 py-2 px-3 bg-gray-50 rounded">
                <FiPackage className="text-gray-500 mr-2" size={16} />
                <span className="text-sm text-gray-700">
                  <span className="font-semibold">{category.product_count || 0}</span> бараа
                </span>
              </div>

              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-xs text-gray-500">
                  {new Date(category.created_at).toLocaleDateString('mn-MN')}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Засах"
                  >
                    <FiEdit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Устгах"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingCategory ? 'Ангилал засах' : 'Шинэ ангилал нэмэх'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Ангилалын нэр <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input"
                    placeholder="Жишээ: ЦАХИЛГААН"
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Тайлбар
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="input"
                    rows="3"
                    placeholder="Ангилалын тайлбар..."
                  ></textarea>
                </div>

                {editingCategory && editingCategory.product_count > 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-sm text-yellow-800">
                      <strong>Анхааруулга:</strong> Энэ ангилалд {editingCategory.product_count} бараа бүртгэлтэй байна. 
                      Нэр өөрчлөгдөх үед бүх барааны ангилал шинэчлэгдэнэ.
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Хадгалж байна...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      {editingCategory ? 'Шинэчлэх' : 'Хадгалах'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;